import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Download } from "lucide-react";

import { Button } from "../../components/common/Button";
import { Input } from "../../components/common/Input";
import { Modal } from "../../components/common/Modal";
import { queueDownload } from "../../api/recordings";

interface Props {
  open: boolean;
  onClose: () => void;
  deviceId: number;
  channel: number;
  initialStart: string;
  initialEnd: string;
}

// El input datetime-local espera "YYYY-MM-DDTHH:MM" (sin segundos).
// El selection.start del Timeline viene como "YYYY-MM-DDTHH:MM:SS" → truncar.
function toInputValue(iso: string): string {
  return iso.length >= 16 ? iso.slice(0, 16) : iso;
}

// Backend (Dahua) espera con segundos. Si el user borra los segundos lo
// completamos con ":00".
function toBackendValue(input: string): string {
  if (!input) return input;
  return input.length === 16 ? `${input}:00` : input;
}

export function DownloadRangeModal({
  open,
  onClose,
  deviceId,
  channel,
  initialStart,
  initialEnd,
}: Props) {
  const [start, setStart] = useState(toInputValue(initialStart));
  const [end, setEnd] = useState(toInputValue(initialEnd));
  const [format, setFormat] = useState<"mp4" | "avi">("mp4");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setStart(toInputValue(initialStart));
      setEnd(toInputValue(initialEnd));
      setFormat("mp4");
      setSubmitting(false);
    }
  }, [open, initialStart, initialEnd]);

  const error = (() => {
    if (!start || !end) return "Completá ambas horas";
    if (end <= start) return "La hora de fin debe ser mayor que la de inicio";
    return null;
  })();

  const onConfirm = async () => {
    if (error) return;
    setSubmitting(true);
    try {
      const t = await queueDownload({
        device_id: deviceId,
        channel,
        start: toBackendValue(start),
        end: toBackendValue(end),
        format,
      });
      toast.success(`Descarga encolada (#${t.id}) — ver progreso en "Descargas"`);
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Fallo al encolar descarga");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Descargar tramo" width="md">
      <div className="flex flex-col gap-3">
        <p className="text-xs text-muted">
          Ajustá las horas de inicio y fin para acotar la descarga.
          Canal CH{channel}.
        </p>
        <Input
          label="Hora inicio"
          type="datetime-local"
          step={1}
          value={start}
          onChange={(e) => setStart(e.target.value)}
        />
        <Input
          label="Hora fin"
          type="datetime-local"
          step={1}
          value={end}
          onChange={(e) => setEnd(e.target.value)}
        />
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-muted">Formato</span>
          <select
            className="bg-bg border border-line rounded px-2 py-1.5 text-sm"
            value={format}
            onChange={(e) => setFormat(e.target.value as "mp4" | "avi")}
          >
            <option value="mp4">MP4 (recomendado)</option>
            <option value="avi">AVI</option>
          </select>
        </label>
        {error && <div className="text-red-400 text-xs">{error}</div>}
        <div className="flex justify-end gap-2 pt-2 border-t border-line">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            disabled={!!error}
            loading={submitting}
          >
            <Download size={14} /> Descargar
          </Button>
        </div>
      </div>
    </Modal>
  );
}
